import { InternalIrfPublicationProps } from './fetchData';

export interface SplitStudentTheses {
  ownTheses: InternalIrfPublicationProps[];
  supervisedTheses: InternalIrfPublicationProps[];
}

/**
 * Splits a name into its lowercased parts, treating hyphenated components
 * as separate parts so that "Simons-Meier" also yields "simons".
 */
const nameParts = (name: string): string[] =>
  name
    .toLowerCase()
    .split(/[,\s-]+/)
    .filter(Boolean);

/**
 * Checks whether two person names denote the same person, tolerating
 * "Last, First" vs "First Last" format differences.
 */
const namesMatch = (a: string, b: string): boolean => {
  const normalizedA = a.toLowerCase().trim();
  const normalizedB = b.toLowerCase().trim();

  if (normalizedA === normalizedB) return true;

  const partsA = normalizedA.split(/[,\s]+/).filter(Boolean);
  const partsB = normalizedB.split(/[,\s]+/).filter(Boolean);

  if (partsA.length >= 2 && partsB.length >= 2) {
    return (
      partsA.every((part) => partsB.includes(part)) &&
      partsB.every((part) => partsA.includes(part))
    );
  }

  return false;
};

/**
 * Relaxed counterpart of namesMatch: true when all parts of one name are
 * contained in the other. The IRF people datastore and the middleware
 * authors array are maintained separately, so one side may carry a second
 * given name ("Rotzetter, Fabienne Maria") or a double last name
 * ("Simons-Meier, Gerd") that the other does not know.
 */
const isNameVariantOf = (a: string, b: string): boolean => {
  const partsA = nameParts(a);
  const partsB = nameParts(b);

  if (!partsA.length || !partsB.length) return false;

  return (
    partsA.every((part) => partsB.includes(part)) ||
    partsB.every((part) => partsA.includes(part))
  );
};

/**
 * Checks whether the given person authored the thesis entry.
 *
 * The authors array from the IRF middleware uses the same "Last, First"
 * format as the people vocabulary and settles the common case. Where it
 * does not, the citation text is consulted as well: the two sources drift,
 * and a thesis whose authors entry carries a second given name is still
 * the person's own work. The citation is matched against the full name and
 * against the abbreviated forms "Last, F." (APA) and "F. Last" (IEEE).
 *
 * Those abbreviations are ambiguous - "Rotzetter, F." fits Fabienne as
 * well as Felix - so they are only trusted when at least one listed author
 * is a plausible variant of the person's name. Without that guard a
 * namesake would inherit the thesis, and a supervisor named in the
 * citation would be credited as its author.
 */
export const isThesisAuthoredByPerson = (
  entry: InternalIrfPublicationProps,
  personName: string,
): boolean => {
  if (!personName) return false;

  if (entry.authors?.some((author) => namesMatch(author, personName))) {
    return true;
  }

  if (
    entry.authors?.length &&
    !entry.authors.some((author) => isNameVariantOf(author, personName))
  ) {
    return false;
  }

  const citation = entry.citation_entry?.toLowerCase() || '';
  if (!citation) return false;

  const normalizedPerson = personName.toLowerCase().trim();
  if (citation.includes(normalizedPerson)) return true;

  const [lastName, firstName] = normalizedPerson
    .split(',')
    .map((part) => part.trim());
  if (!lastName || !firstName) return false;

  const firstInitial = firstName[0];
  return (
    citation.includes(`${lastName}, ${firstInitial}.`) ||
    citation.includes(`${firstInitial}. ${lastName}`)
  );
};

/**
 * Legacy split used when the person's name is not available and only the
 * IRF handle (e.g. "11654/35977") is known: authorship has to be guessed
 * from the citation text. Kept as fallback for backwards compatibility.
 */
const splitStudentThesesByHeuristic = (
  theses: InternalIrfPublicationProps[],
  person: string,
): SplitStudentTheses => {
  const ownTheses: InternalIrfPublicationProps[] = [];
  const supervisedTheses: InternalIrfPublicationProps[] = [];

  /**
   * Helper function to check if a person is related to a thesis
   * Checks both authors array and citation entry for person's name
   * Handles different name formats (Last, First vs First Last)
   */
  const isPersonRelatedToThesis = (
    entry: InternalIrfPublicationProps,
  ): boolean => {
    // Check authors array with exact match first,
    // then with normalized comparison for format variations
    const isInAuthors =
      entry.authors?.some((author) => namesMatch(author, person)) ?? false;
    if (isInAuthors) return true;

    // Check citation entry with exact match first
    const isInCitationExact = entry.citation_entry?.includes(person) ?? false;
    if (isInCitationExact) return true;

    // Check citation entry with normalized comparison
    return (
      entry.citation_entry?.toLowerCase().includes(person.toLowerCase()) ??
      false
    );
  };

  // Extract person's lastName for citation matching
  const personParts = person.split(/[,\s]+/).filter(Boolean);
  const personLastName = personParts[0]?.toLowerCase().trim();

  theses.forEach((entry: InternalIrfPublicationProps) => {
    // Check if person is related to thesis (author or mentioned in citation)
    if (isPersonRelatedToThesis(entry)) {
      // Case 1: Person is author or mentioned - goes to Publications tab
      ownTheses.push(entry);
      return;
    }

    const citationLower = entry.citation_entry?.toLowerCase() || '';

    // Case 2: Check if person's lastName appears in citation
    const hasPersonLastNameInCitation =
      personLastName && citationLower.includes(personLastName);

    // Case 3: Check if SINGLE author appears in citation
    const hasAuthorInCitation =
      (entry.authors?.length === 1 &&
        entry.authors?.some((author) => {
          if (!author || !entry.citation_entry) return false;

          const authorParts = author.split(',').map((part) => part.trim());
          if (authorParts.length < 2) return false;

          const lastName = authorParts[0].toLowerCase();
          const firstInitial = authorParts[1].toLowerCase()[0];

          // Check if author appears in citation
          // (e.g., "Guasso, Chiara" -> "guasso, c.")
          return citationLower.includes(`${lastName}, ${firstInitial}.`);
        })) ??
      false;

    // Only move to Publications if:
    // 1. Person's lastName in citation, OR
    // 2. Author in citation AND it's peer reviewed OR invented_here is false
    if (
      hasPersonLastNameInCitation ||
      (hasAuthorInCitation &&
        (entry.peer_reviewed || entry.invented_here === false))
    ) {
      ownTheses.push(entry);
    } else {
      supervisedTheses.push(entry);
    }
  });

  return { ownTheses, supervisedTheses };
};

/**
 * Splits student theses (type 11) into the person's own theses (shown in
 * the publications tab) and theses the person supervised (shown in the
 * supervised theses tab).
 *
 * The person is the author of their own thesis, while supervised theses
 * are authored by the supervised students, so authorship decides the tab.
 * The block stores the person as IRF handle; the backend resolves it to
 * the person's name (personName). Without a resolved name the legacy
 * citation heuristic is used.
 */
export const splitStudentTheses = (
  theses: InternalIrfPublicationProps[],
  person: string | null,
  personName: string | null,
): SplitStudentTheses => {
  if (!person) {
    // No person selected - all theses go to the supervised theses tab
    return { ownTheses: [], supervisedTheses: theses };
  }

  if (personName) {
    const ownTheses: InternalIrfPublicationProps[] = [];
    const supervisedTheses: InternalIrfPublicationProps[] = [];

    theses.forEach((entry) => {
      if (isThesisAuthoredByPerson(entry, personName)) {
        ownTheses.push(entry);
      } else {
        supervisedTheses.push(entry);
      }
    });

    return { ownTheses, supervisedTheses };
  }

  return splitStudentThesesByHeuristic(theses, person);
};
